import { downloadFile, requestJson } from "@/lib/api";

// ---- Types (mirror the backend selection DTOs) ----

export type ProcessStatus = "RASCUNHO" | "ABERTO" | "ENCERRADO" | "ARQUIVADO";
export type EnrollmentStatus =
  | "EM_PREENCHIMENTO"
  | "RESPOSTAS_ENVIADAS"
  | "EM_AVALIACAO"
  | "CONCLUIDA";
export type EvaluationStatus = "EM_AVALIACAO" | "CONCLUIDA";
export type SelectionDecision = "NAO_APROVAR" | "MANTER" | "APROVAR" | "PRIORIDADE";
export type Compatibility = "NAO" | "POUCO" | "COMPATIVEL" | "MUITO" | "EXCELENTE";
export type StageResult = "AVANCAR" | "AVALIAR" | "NAO_AVANCAR";
export type TiebreakConfidence =
  | "SIM_SEM_HESITACAO"
  | "SIM_COM_ACOMPANHAMENTO"
  | "TENHO_DUVIDAS"
  | "NAO";
export type Visibility = "CANDIDATE" | "EVALUATOR" | "BOTH";
export type AnswerType =
  | "SHORT_TEXT"
  | "LONG_TEXT"
  | "SINGLE_CHOICE"
  | "MULTI_CHOICE"
  | "YES_NO"
  | "SCORE"
  | "DECISION";

export interface ProcessSummary {
  id: string;
  name: string;
  jobName: string | null;
  status: ProcessStatus;
  activeVersionNumber: number;
  candidateCount: number;
  createdAt: string;
  openedAt: string | null;
  closedAt: string | null;
}

export interface ProcessDetail extends ProcessSummary {
  description: string | null;
  internalNotes: string | null;
  responsibles: string | null;
  activeVersionId: string;
  activeVersionState: "DRAFT" | "FROZEN";
}

export interface CandidateRow {
  enrollmentId: string;
  candidateName: string;
  accessCode: string;
  status: EnrollmentStatus;
  finalScore: number | null;
  evaluatedWeightPct: number | null;
  partialScore: boolean;
  decision: SelectionDecision | null;
}

export interface CreatedCandidate {
  enrollmentId: string;
  candidateName: string;
  accessCode: string;
}

export interface EvaluatorQuestion {
  id: string;
  orderIndex: number;
  statement: string;
  answerType: AnswerType;
  visibility: Visibility;
  options: string | null;
  meta: string | null;
  helpText?: string | null;
  candidateValueText: string | null;
  candidateValueJson: string | null;
  evaluatorValueText: string | null;
  evaluatorValueJson: string | null;
  evaluatorScore: number | null;
}

export interface EvaluatorStage {
  id: string;
  orderIndex: number;
  stageKey: string;
  title: string;
  visibility: Visibility;
  meta: string | null;
  result: StageResult | null;
  note: string | null;
  questions: EvaluatorQuestion[];
}

export interface CriterionView {
  criterionId: string;
  criterionKey: string;
  label: string;
  orderIndex: number;
  weightPct: number;
  scaleMin: number;
  scaleMax: number;
  score: number | null;
}

export interface ScoreView {
  finalScore: number | null;
  evaluatedWeightPct: number | null;
  partial: boolean;
}

export interface EvaluatorFicha {
  enrollmentId: string;
  candidateName: string;
  accessCode: string;
  enrollmentStatus: EnrollmentStatus;
  evaluationStatus: EvaluationStatus | null;
  stages: EvaluatorStage[];
  criteria: CriterionView[];
  score: ScoreView;
  practicalScore: number | null;
  potential: number | null;
  compatibility: Compatibility | null;
  decision: SelectionDecision | null;
  tiebreakAnswer: TiebreakConfidence | null;
  alerts: string | null;
  strengths: string | null;
  devPoints: string | null;
  generalOpinion: string | null;
  concludedByAccountId: string | null;
  concludedByName: string | null;
  concludedAt: string | null;
}

export interface RankingCell {
  criterionKey: string;
  label: string;
  score: number | null;
}

export interface RankingRow {
  position: number;
  enrollmentId: string;
  candidateName: string;
  finalScore: number | null;
  partialScore: boolean;
  potential: number | null;
  decision: SelectionDecision | null;
  criteria: RankingCell[];
}

export interface ConfigView {
  allowCandidateInterviewWriteup: boolean;
  hasPin: boolean;
}

export interface SaveEvaluationRequest {
  stageResults?: { stageId: string; result: StageResult | null; note: string | null }[];
  criterionScores?: { criterionId: string; score: number | null }[];
  evaluatorAnswers?: { questionId: string; valueText: string | null; valueJson: string | null; score: number | null }[];
  parecer?: {
    practicalScore: number | null;
    potential: number | null;
    compatibility: Compatibility | null;
    decision: SelectionDecision | null;
    tiebreakAnswer: TiebreakConfidence | null;
    alerts: string | null;
    strengths: string | null;
    devPoints: string | null;
    generalOpinion: string | null;
  };
}

const BASE = "/api/v1/admin/selecao";

// ---- Processes ----
export const fetchProcesses = () => requestJson<ProcessSummary[]>(`${BASE}/processos`);
export const createProcess = (body: { name: string; jobName?: string | null; description?: string | null }) =>
  requestJson<ProcessDetail>(`${BASE}/processos`, { method: "POST", body });
export const fetchProcess = (id: string) => requestJson<ProcessDetail>(`${BASE}/processos/${id}`);
export const openProcess = (id: string) => requestJson<ProcessDetail>(`${BASE}/processos/${id}/abrir`, { method: "POST" });
export const closeProcess = (id: string) => requestJson<ProcessDetail>(`${BASE}/processos/${id}/encerrar`, { method: "POST" });
export const archiveProcess = (id: string) => requestJson<ProcessDetail>(`${BASE}/processos/${id}/arquivar`, { method: "POST" });
export const reopenProcess = (id: string) => requestJson<ProcessDetail>(`${BASE}/processos/${id}/reabrir`, { method: "POST" });
export const newDraftVersion = (id: string) => requestJson<unknown>(`${BASE}/processos/${id}/nova-versao`, { method: "POST" });

// ---- Candidates ----
export const fetchCandidates = (processId: string) =>
  requestJson<CandidateRow[]>(`${BASE}/processos/${processId}/candidatos`);
export const createCandidate = (processId: string, name: string) =>
  requestJson<CreatedCandidate>(`${BASE}/processos/${processId}/candidatos`, { method: "POST", body: { name } });

// ---- Evaluation ----
export const fetchFicha = (enrollmentId: string) =>
  requestJson<EvaluatorFicha>(`${BASE}/candidatos/${enrollmentId}`);
export const saveEvaluation = (enrollmentId: string, body: SaveEvaluationRequest) =>
  requestJson<EvaluatorFicha>(`${BASE}/candidatos/${enrollmentId}/avaliacao`, { method: "PUT", body });
export const concludeEvaluation = (enrollmentId: string) =>
  requestJson<EvaluatorFicha>(`${BASE}/candidatos/${enrollmentId}/concluir`, { method: "POST" });

// ---- Ranking ----
export const fetchRanking = (processId: string) =>
  requestJson<RankingRow[]>(`${BASE}/processos/${processId}/ranking`);
export const downloadRankingCsv = (processId: string) =>
  downloadFile(`${BASE}/processos/${processId}/ranking.csv`, "ranking.csv");
export const downloadFichaCsv = (enrollmentId: string) =>
  downloadFile(`${BASE}/candidatos/${enrollmentId}/ficha.csv`, "ficha.csv");

// ---- Config / PIN ----
export const fetchConfig = (processId: string) =>
  requestJson<ConfigView>(`${BASE}/processos/${processId}/ajustes`);
export const updateConfig = (processId: string, allowCandidateInterviewWriteup: boolean) =>
  requestJson<ConfigView>(`${BASE}/processos/${processId}/ajustes`, {
    method: "PUT",
    body: { allowCandidateInterviewWriteup },
  });
export const changePin = (processId: string, pin: string) =>
  requestJson<void>(`${BASE}/processos/${processId}/pin`, { method: "PUT", body: { pin } });
